import { defineStore } from 'pinia'
import SettingsRepository from '@/repositories/SettingsRepository'
import { WpClient } from "@/types/WpClient"
import { ClientDictionary } from "@/types/ClientDiccionary"
import { Constants } from "@/constants/Constants"
import ToastService from "@/services/ToastService"
import i18n from "@/plugins/i18n"
import WhatsAppClient from "@/services/gordaApi/WhatsAppClient"

export const useWpClientsStore = defineStore('settingsStore', {
  state: () => {
    return {
      clients: {} as ClientDictionary,
      defaultClient: null as string | null,
      busy: {} as Record<string, boolean>
    }
  },
  actions: {
    enableWpNotifications(client: WpClient, enabled: boolean): void {
      if (enabled && client.chatBot) {
        ToastService.toast(
          ToastService.ERROR,
          i18n.global.t('common.messages.error'),
          i18n.global.t('common.messages.error_chatBot')
        )

        return
      }
      this.busy[client.id] = true
      SettingsRepository.enableWpNotifications(client.id, enabled).then(() => {
        this.clients[client.id].wpNotifications = enabled
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error_chatBot'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    getWpClient(clientId: string): WpClient | null {
      return this.clients[clientId] ?? null
    },

    enableChatBot(client: WpClient, enabled: boolean): void {
      if (enabled && (client.assistant || client.wpNotifications)) {
        ToastService.toast(
          ToastService.ERROR,
          i18n.global.t('common.messages.error'),
          i18n.global.t('common.messages.error_chatBot')
        )

        return
      }
      this.busy[client.id] = true
      SettingsRepository.enableChatBot(client.id, enabled).then(async () => {
        this.clients[client.id].chatBot = enabled
        if (enabled) {
          await SettingsRepository.enableAssistant(client.id, false)
        }
      })
        .catch(async () => {
          await ToastService.toast(
            ToastService.ERROR,
            i18n.global.t('common.messages.error'),
            i18n.global.t('common.messages.error_chatBot')
          )
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    enableAssistant(client: WpClient, enabled: boolean): void {
      if (enabled && client.chatBot) {
        ToastService.toast(
          ToastService.ERROR,
          i18n.global.t('common.messages.error'),
          i18n.global.t('common.messages.error_chatBot')
        )

        return
      }
      this.busy[client.id] = true
      SettingsRepository.enableAssistant(client.id, enabled).then(() => {
        this.clients[client.id].assistant = enabled
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    enableFull(client: WpClient, enabled: boolean): void {
      this.busy[client.id] = true
      SettingsRepository.enableFull(client.id, enabled).then(() => {
        this.clients[client.id].full = enabled
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    enableAgentInTrip(client: WpClient, enabled: boolean): void {
      this.busy[client.id] = true
      SettingsRepository.enableAgentInTrip(client.id, enabled).then(() => {
        this.clients[client.id].agentInTrip = enabled
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    async getWpClients(): Promise<void> {
      this.clients = await SettingsRepository.getWpClients()
      if (Object.values(this.clients).length) {
        this.defaultClient = sessionStorage.getItem(Constants.DEFAULT_CLIENT) ??
          Object.values(this.clients)[0].id

        this.setDefault(this.defaultClient)
      } else {
        sessionStorage.removeItem(Constants.DEFAULT_CLIENT)
      }
    },

    setDefault(client: string): void {
      this.defaultClient = client
      sessionStorage.setItem(Constants.DEFAULT_CLIENT, client)
    },

    getDefault(): WpClient {
      return this.clients[this.defaultClient as string]
    },

    onWpNotification(client: WpClient): void {
      SettingsRepository.onWpNotifications(client, () => undefined)
    },

    offWpNotifications(client: WpClient): void {
      SettingsRepository.offWpNotifications(client)
    },

    async createClient(client: WpClient): Promise<void> {
      this.busy[client.id] = true
      await SettingsRepository.createClient(client).then(() => {
        this.clients[client.id] = {
          id: client.id,
          alias: client.alias,
          full: false,
          wpNotifications: false,
          chatBot: false,
          assistant: false,
          agentInTrip: false,
          service: client.service
        }
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    },

    async deleteClient(client: WpClient): Promise<void> {
      this.busy[client.id] = true
      await SettingsRepository.deleteClient(client).then(() => {
        delete this.clients[client.id]
        if (client.id === this.defaultClient) {
          this.defaultClient = null
          sessionStorage.removeItem(Constants.DEFAULT_CLIENT)
          const socket = WhatsAppClient.getInstance(client)
          socket.clientDeleted()
        }
      })
        .catch(async (e) => {
          await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), e.message)
        })
        .finally(() => {
          delete this.busy[client.id]
        })
    }
  }
})
